import { Search, Loader2 } from "lucide-react";
import { formatFiatWithTwoDecimals } from "../utils/numberFormatter";

interface Asset {
  assetId: string;
  asset: string;
  assetIcon?: string;
  assetName?: string;
  availableAmount?: number;
  availableAmountFormatted?: string;
  valueInLocalCurrency?: string;
  [key: string]: unknown;
}

interface AssetSelectionContentProps {
  searchTerm: string;
  onSearchChange: (value: string) => void;
  filteredAssets: Asset[] | undefined;
  onAssetSelect: (asset: Asset) => void;
  showBalance?: boolean;
  formatter?: {
    format: (amount: number | undefined, currency: string) => string;
  };
  isLoading?: boolean;
}

export default function AssetSelectionContent({
  searchTerm,
  onSearchChange,
  filteredAssets,
  onAssetSelect,
  showBalance = false,
  formatter,
  isLoading = false,
}: AssetSelectionContentProps) {
  return (
    <div className="flex flex-col">
      <div className="mb-4">
        <div className="flex items-center gap-2 relative">
          <Search className="w-4 h-4 text-text-secondary absolute left-4" />
          <input
            type="text"
            placeholder="Search"
            className="w-full border rounded-full px-8 py-3 pl-10 text-sm"
            value={searchTerm}
            onChange={(e) => onSearchChange(e.target.value)}
          />
        </div>
      </div>
      <div className="flex flex-col space-y-3 max-h-72 overflow-y-auto">
        {isLoading ? (
          <div className="flex flex-col items-center justify-center py-8">
            <Loader2 className="h-8 w-8 animate-spin text-brand-primary mb-3" />
            <p className="text-sm text-gray-500">Loading assets...</p>
          </div>
        ) : (
          filteredAssets?.map((asset: Asset) => {
            return (
              <button
                key={asset.assetId}
                onClick={() => onAssetSelect(asset)}
                className={`flex items-center gap-3 px-3 py-3 border rounded-lg border-transparent bg-gray-50 hover:border-rose-200 ${
                  showBalance ? "justify-between" : ""
                }`}
              >
                <div className="flex items-center gap-3">
                  <img
                    src={asset.assetIcon}
                    alt={asset.assetName}
                    className="w-6 h-6"
                  />
                  <div className="flex flex-col text-left">
                    <span>{asset.assetName}</span>
                    <span className="text-xs text-gray-500">
                      {asset.asset.toUpperCase()}
                    </span>
                  </div>
                </div>
                {showBalance && formatter && (
                  <div className="flex flex-col text-right">
                    <span className="font-semibold text-text-primary text-sm">
                      {formatter.format(
                        asset.availableAmount,
                        asset.asset.toUpperCase()
                      )}
                    </span>
                    <span className="text-xs text-text-secondary">
                      {asset.valueInLocalCurrency
                        ? formatFiatWithTwoDecimals(asset.valueInLocalCurrency)
                        : "R 0.00"}
                    </span>
                  </div>
                )}
              </button>
            );
          })
        )}
      </div>
    </div>
  );
}
